/**
 * phaseFieldCrystalPipeline — Phase Field Crystal (PFC) simulation.
 *
 * The PFC equation: ∂φ/∂t = ∇²φ + (ε - |∇²|²)φ - φ³. A density
 * field that spontaneously forms crystalline patterns. PFC is the
 * simplest equation that produces crystal-like self-assembly.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface PhaseFieldCrystalPipeline {
  updateProgram: WebGLProgram | null;
  renderProgram: WebGLProgram | null;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  stateTextures: [WebGLTexture | null, WebGLTexture | null];
  stateBuffers: [WebGLFramebuffer | null, WebGLFramebuffer | null];
  pingIdx: number;
  bands: Float32Array;
  gridSize: number;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const UPDATE_FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outState;

uniform sampler2D uPrevState;
uniform float uTime;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

void main() {
  vec2 texel = 1.0 / vec2(textureSize(uPrevState, 0));
  float phi = texture(uPrevState, vUV).r;
  // Compute the Laplacian (5-point stencil).
  float phiL = texture(uPrevState, vUV - vec2(texel.x, 0.0)).r;
  float phiR = texture(uPrevState, vUV + vec2(texel.x, 0.0)).r;
  float phiB = texture(uPrevState, vUV - vec2(0.0, texel.y)).r;
  float phiT = texture(uPrevState, vUV + vec2(0.0, texel.y)).r;
  float lap = phiL + phiR + phiB + phiT - 4.0 * phi;
  // Compute the biharmonic operator (∇⁴).
  // Approximate via Laplacian of Laplacian.
  float phiLL = texture(uPrevState, vUV - vec2(2.0 * texel.x, 0.0)).r;
  float phiRR = texture(uPrevState, vUV + vec2(2.0 * texel.x, 0.0)).r;
  float phiBB = texture(uPrevState, vUV - vec2(0.0, 2.0 * texel.y)).r;
  float phiTT = texture(uPrevState, vUV + vec2(0.0, 2.0 * texel.y)).r;
  float lapLap = phiLL + phiRR + phiBB + phiTT + 2.0 * (phiL + phiR + phiB + phiT) - 12.0 * phi;
  // PFC equation: dphi/dt = lap + (eps - lap*lap) * phi - phi^3
  // For simplicity, just use: dphi/dt = lapLap + eps * phi - phi^3
  float eps = 0.5;
  float dphi = lapLap * 0.05 + eps * phi - phi * phi * phi;
  float phiNew = phi + dphi * 0.1;
  outState = vec4(phiNew, 0.0, 0.0, 1.0);
}
`;

const RENDER_FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform sampler2D uState;
uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

void main() {
  vec2 texel = 1.0 / vec2(textureSize(uState, 0));
  float phi = texture(uState, vUV).r;
  // Compute phase gradient for crystal pattern detection.
  vec2 grad = vec2(
    texture(uState, vUV + vec2(texel.x, 0.0)).r - texture(uState, vUV - vec2(texel.x, 0.0)).r,
    texture(uState, vUV + vec2(0.0, texel.y)).r - texture(uState, vUV - vec2(0.0, texel.y)).r
  );
  // Visualization: crystal lattice sites appear as bright dots.
  float crystal = sin(phi * 12.0) * sin(phi * 8.0 + 1.0);
  crystal = max(crystal, 0.0);
  // Background: deep purple gradient.
  vec2 p = vUV * 2.0 - 1.0;
  p.x *= uResolution.x / uResolution.y;
  vec3 bg = vec3(0.04, 0.02, 0.10);
  vec3 col = bg + vec3(0.10, 0.05, 0.20) * (1.0 - length(p) * 0.5);
  // Crystal pattern.
  vec3 crystalColor = vec3(0.95, 0.45, 0.85);
  col += crystalColor * crystal * 0.8;
  // Gradient norm creates bright edges.
  float gradMag = length(grad);
  col += vec3(0.95, 0.85, 0.40) * gradMag * 4.0;
  // Audio modulation.
  float bassAvg = (uBass.x + uBass.y + uBass.z) / 3.0;
  float midAvg = (uMid.x + uMid.y + uMid.z) / 3.0;
  col *= 1.0 + bassAvg * 0.5;
  col += vec3(0.40, 0.85, 0.95) * midAvg * 0.2;
  outColor = vec4(col, 1.0);
}
`;

function createStateTexture(gl: WebGL2RenderingContext, size: number, seed: number): WebGLTexture {
  const tex = gl.createTexture();
  if (!tex) throw new Error("Failed to create texture");
  gl.bindTexture(gl.TEXTURE_2D, tex);
  const pixels = new Float32Array(size * size * 4);
  let s = seed | 0;
  function rand() {
    s = (s + 0x6D2B79F5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }
  for (let i = 0; i < size * size; i++) {
    // Initial: small random perturbation around phi = 0.
    pixels[i * 4 + 0] = (rand() - 0.5) * 0.4;
    pixels[i * 4 + 1] = 0;
    pixels[i * 4 + 2] = 0;
    pixels[i * 4 + 3] = 1;
  }
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, size, size, 0, gl.RGBA, gl.FLOAT, pixels);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
  gl.bindTexture(gl.TEXTURE_2D, null);
  return tex;
}

function attachFbo(gl: WebGL2RenderingContext, tex: WebGLTexture, size: number): WebGLFramebuffer {
  const fb = gl.createFramebuffer();
  if (!fb) throw new Error("Failed to create framebuffer");
  gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
  gl.viewport(0, 0, size, size);
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  return fb;
}

export function createPhaseFieldCrystalPipeline(gl: WebGL2RenderingContext, size = 96): PhaseFieldCrystalPipeline | null {
  const updateProgram = linkProgram(gl, VERT_SRC, UPDATE_FRAG_SRC, POST_ATTRIBS);
  const renderProgram = linkProgram(gl, VERT_SRC, RENDER_FRAG_SRC, POST_ATTRIBS);
  if (!updateProgram || !renderProgram) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);

  const tex0 = createStateTexture(gl, size, 7);
  const tex1 = createStateTexture(gl, size, 7);
  const fb0 = attachFbo(gl, tex0, size);
  const fb1 = attachFbo(gl, tex1, size);

  return {
    updateProgram, renderProgram, vao, vbo,
    stateTextures: [tex0, tex1],
    stateBuffers: [fb0, fb1],
    pingIdx: 0,
    bands: new Float32Array(9),
    gridSize: size,
  };
}

export function computePhaseFieldCrystalBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computePhaseFieldCrystalRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function drawPhaseFieldCrystal(
  gl: WebGL2RenderingContext,
  pipe: PhaseFieldCrystalPipeline,
  width: number,
  height: number,
  t: number,
): void {
  if (!pipe.updateProgram || !pipe.renderProgram) return;
  const size = pipe.gridSize;
  for (let i = 0; i < 8; i++) {
    const srcIdx = pipe.pingIdx;
    const dstIdx = 1 - pipe.pingIdx;
    const srcTex = pipe.stateTextures[srcIdx];
    const dstFb = pipe.stateBuffers[dstIdx];
    if (!srcTex || !dstFb) continue;
    gl.bindFramebuffer(gl.FRAMEBUFFER, dstFb);
    gl.viewport(0, 0, size, size);
    gl.useProgram(pipe.updateProgram);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, srcTex);
    const uPrev = gl.getUniformLocation(pipe.updateProgram, "uPrevState");
    if (uPrev) gl.uniform1i(uPrev, 0);
    const uTime = gl.getUniformLocation(pipe.updateProgram, "uTime");
    if (uTime) gl.uniform1f(uTime, t + Number(i) * 0.1);
    gl.bindVertexArray(pipe.vao);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.bindVertexArray(null);
    pipe.pingIdx = dstIdx;
  }
  const finalTex = pipe.stateTextures[pipe.pingIdx];
  if (!finalTex) return;
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.viewport(0, 0, width, height);
  gl.clearColor(0, 0, 0, 1);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.useProgram(pipe.renderProgram);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, finalTex);
  const uState = gl.getUniformLocation(pipe.renderProgram, "uState");
  if (uState) gl.uniform1i(uState, 0);
  const uResolution = gl.getUniformLocation(pipe.renderProgram, "uResolution");
  if (uResolution) gl.uniform2f(uResolution, width, height);
  const uTime = gl.getUniformLocation(pipe.renderProgram, "uTime");
  if (uTime) gl.uniform1f(uTime, t);
  const uBass = gl.getUniformLocation(pipe.renderProgram, "uBass");
  if (uBass) gl.uniform3f(uBass, pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  const uMid = gl.getUniformLocation(pipe.renderProgram, "uMid");
  if (uMid) gl.uniform3f(uMid, pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  const uHigh = gl.getUniformLocation(pipe.renderProgram, "uHigh");
  if (uHigh) gl.uniform3f(uHigh, pipe.bands[6], pipe.bands[7], pipe.bands[8]);

    gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
